import { PCA } from 'ml-pca';
import { Dataset, Dimension, DimensionGroup } from '../types';

/**
 * Calculate correlation matrix for a set of dimensions
 * @param dataset The dataset to calculate correlations for
 * @param dimensionIds Array of dimension IDs to include
 * @returns Correlation matrix as a 2D array
 */
export function calculateCorrelationMatrix(dataset: Dataset, dimensionIds: string[]): number[][] {
  const dimensions = dataset.dimensions.filter((d) => dimensionIds.includes(d.id));
  const numDimensions = dimensions.length;
  const correlationMatrix: number[][] = Array.from({ length: numDimensions }, () => Array(numDimensions).fill(0));

  // Only process numeric dimensions
  const numericDimensions = dimensions.filter((d) => d.type === 'numeric');
  const numericIndices = numericDimensions.map((d) => dimensions.findIndex((dim) => dim.id === d.id));

  if (numericDimensions.length < 2) {
    return correlationMatrix;
  }

  // Calculate means for each dimension
  const means = numericDimensions.map((d) => {
    return dataset.items.reduce((sum, item) => sum + Number(item[d.id]), 0) / dataset.items.length;
  });

  // Calculate standard deviations
  const stdDevs = numericDimensions.map((d, i) => {
    const mean = means[i];
    const squaredDiffs = dataset.items.map((item) => Math.pow(Number(item[d.id]) - mean, 2));
    const variance = squaredDiffs.reduce((sum, val) => sum + val, 0) / dataset.items.length;
    return Math.sqrt(variance);
  });

  // Calculate correlations (only for numeric dimensions)
  for (let i = 0; i < numericDimensions.length; i++) {
    for (let j = 0; j < numericDimensions.length; j++) {
      if (i === j) {
        correlationMatrix[numericIndices[i]][numericIndices[j]] = 1;
        continue;
      }

      const dimI = numericDimensions[i];
      const dimJ = numericDimensions[j];
      const meanI = means[i];
      const meanJ = means[j];
      const stdDevI = stdDevs[i];
      const stdDevJ = stdDevs[j];

      if (stdDevI === 0 || stdDevJ === 0) {
        correlationMatrix[numericIndices[i]][numericIndices[j]] = 0;
        continue;
      }

      let covariance = 0;
      for (const item of dataset.items) {
        covariance += (Number(item[dimI.id]) - meanI) * (Number(item[dimJ.id]) - meanJ);
      }
      covariance /= dataset.items.length;

      const correlation = covariance / (stdDevI * stdDevJ);
      correlationMatrix[numericIndices[i]][numericIndices[j]] = correlation;
    }
  }

  // Fill in categorical dimensions with 0 correlation (or could do more advanced measures)

  return correlationMatrix;
}

/**
 * Perform PCA on a subset of dimensions
 * @param dataset The dataset to analyze
 * @param dimensionIds Array of dimension IDs to include in the PCA
 * @returns The first principal component vector
 */
export function calculatePrincipalComponent(dataset: Dataset, dimensionIds: string[]): number[] {
  // Filter to only numeric dimensions
  const numericDimensions = dataset.dimensions.filter((d) => dimensionIds.includes(d.id) && d.type === 'numeric');

  if (numericDimensions.length < 2) {
    // Not enough dimensions for PCA
    return dimensionIds.map((id) => {
      const dim = dataset.dimensions.find((d) => d.id === id);
      return dim && dim.type === 'numeric' ? 1 : 0;
    });
  }

  // Extract data for the selected dimensions
  const data = dataset.items.map((item) => numericDimensions.map((dim) => Number(item[dim.id])));

  // Perform PCA
  const pca = new PCA(data);
  const firstPC = pca.getEigenvectors().getRow(0);

  // Map the PC vector back to the original dimensions (including categorical as 0)
  const pcVector = dimensionIds.map((id) => {
    const idx = numericDimensions.findIndex((d) => d.id === id);
    return idx >= 0 ? firstPC[idx] : 0;
  });

  return pcVector;
}

/**
 * Project data items to 1D using the provided projection vector
 * @param dataset The dataset containing items to project
 * @param dimensionIds Dimensions to use in the projection
 * @param projectionVector Vector to use for projection (if omitted, will calculate the first PC)
 * @returns Array of projected values for each data item
 */
export function projectTo1D(dataset: Dataset, dimensionIds: string[], projectionVector?: number[]): number[] {
  // Get or calculate projection vector
  const vector = projectionVector || calculatePrincipalComponent(dataset, dimensionIds);

  // Only use numeric dimensions for the projection
  const numericDimIds = dataset.dimensions
    .filter((d) => dimensionIds.includes(d.id) && d.type === 'numeric')
    .map((d) => d.id);

  // Project each data item
  return dataset.items.map((item) => {
    let projectedValue = 0;
    numericDimIds.forEach((dimId, i) => {
      const dimIndex = dimensionIds.indexOf(dimId);
      if (dimIndex >= 0) {
        projectedValue += Number(item[dimId]) * vector[dimIndex];
      }
    });
    return projectedValue;
  });
}

/**
 * Create dimension groups based on correlation structure
 * @param dataset The dataset to analyze
 * @param threshold Correlation threshold for grouping dimensions
 * @returns Array of dimension groups
 */
export function createDimensionGroups(dataset: Dataset, threshold: number = 0.7): DimensionGroup[] {
  const allDimIds = dataset.dimensions.map((d) => d.id);
  const correlationMatrix = calculateCorrelationMatrix(dataset, allDimIds);
  const dimensionMap = new Map<string, Dimension>();
  dataset.dimensions.forEach((d) => dimensionMap.set(d.id, d));

  // Track which dimensions have been assigned to groups
  const assigned = new Set<string>();
  const groups: DimensionGroup[] = [];

  // First pass: find highly correlated groups
  for (let i = 0; i < dataset.dimensions.length; i++) {
    const dimI = dataset.dimensions[i];
    if (assigned.has(dimI.id) || dimI.type !== 'numeric') continue;

    const groupDimensions: string[] = [dimI.id];
    assigned.add(dimI.id);

    for (let j = 0; j < dataset.dimensions.length; j++) {
      const dimJ = dataset.dimensions[j];
      if (i === j || assigned.has(dimJ.id) || dimJ.type !== 'numeric') continue;

      const correlation = Math.abs(correlationMatrix[i][j]);
      if (correlation >= threshold) {
        groupDimensions.push(dimJ.id);
        assigned.add(dimJ.id);
      }
    }

    if (groupDimensions.length > 1) {
      // Create a group for correlated dimensions
      const groupMatrix = calculateCorrelationMatrix(dataset, groupDimensions);
      const projectionVector = calculatePrincipalComponent(dataset, groupDimensions);

      groups.push({
        id: `group-${groups.length + 1}`,
        name: `Group ${groups.length + 1}`,
        dimensions: groupDimensions,
        correlationMatrix: groupMatrix,
        projectionVector,
      });
    }
  }

  // Second pass: handle remaining dimensions
  dataset.dimensions.forEach((dim) => {
    if (!assigned.has(dim.id)) {
      groups.push({
        id: `group-${groups.length + 1}`,
        name: dim.name,
        dimensions: [dim.id],
      });
      assigned.add(dim.id);
    }
  });

  return groups;
}

/**
 * Create a hierarchical structure of dimension groups
 * @param dataset The dataset to analyze
 * @param levels Number of hierarchy levels to create
 * @returns Object with hierarchy levels
 */
export function createHierarchy(dataset: Dataset, levels: number = 2): Record<string, any> {
  const hierarchyLevels: Record<string, any> = {};

  // Level 0: All dimensions as one group
  const allDimIds = dataset.dimensions.map((d) => d.id);
  hierarchyLevels['level-0'] = {
    id: 'level-0',
    name: 'Overview',
    dimensionGroups: [
      {
        id: 'all-dimensions',
        name: 'All Dimensions',
        dimensions: allDimIds,
        correlationMatrix: calculateCorrelationMatrix(dataset, allDimIds),
        projectionVector: calculatePrincipalComponent(dataset, allDimIds),
      },
    ],
    childLevels: ['level-1'],
  };

  // Level 1: Grouped by correlation (medium threshold)
  const level1Groups = createDimensionGroups(dataset, 0.7);
  hierarchyLevels['level-1'] = {
    id: 'level-1',
    name: 'Main Groups',
    dimensionGroups: level1Groups,
    parentLevel: 'level-0',
    childLevels: level1Groups.length > 5 ? ['level-2'] : undefined,
  };

  // Optional level 2: More fine-grained groups
  if (levels > 2 && level1Groups.length > 5) {
    const level2Groups = createDimensionGroups(dataset, 0.85);
    hierarchyLevels['level-2'] = {
      id: 'level-2',
      name: 'Detailed Groups',
      dimensionGroups: level2Groups,
      parentLevel: 'level-1',
    };
  }

  return hierarchyLevels;
}
